import { toast } from 'svelte-sonner';

import { error } from '@sveltejs/kit';
import { get } from 'svelte/store';

import { apiErrorMode } from '$lib/api-error-handling';
import { domainErrorKind } from '$lib/api-error-kind';
import type { ClientMeta } from '$lib/api/client';
import { client as apiClient } from '$lib/api/client.gen';
import { isLocal } from '$lib/build-info';
import { versionRequestHeaderValue } from '$lib/build-info';
import maybeDelay from '$lib/dev-delay';
import { describeError } from '$lib/error-details';
import {
	refreshUser,
	signOut,
	user as authUser,
	useCredentialsIncludeRequestInterceptor,
} from '$lib/stores/auth';
import errorStore, { AppError } from '$lib/stores/global-error';
import { refusedRun, refusedRunOf } from '$lib/stores/refused-run';
import versionPolicy, { parseVersionPolicyHeaderValue } from '$lib/stores/version-policy';

type VersionPolicyHeaderStatus = 'ok' | 'warn' | 'required' | 'policy-unavailable';

function isAbortError(err: unknown, req: Request | undefined): boolean {
	if (req?.signal.aborted) return true;
	if (!err || typeof err !== 'object') return false;

	const errorLike = err as { name?: unknown; code?: unknown };
	return errorLike.name === 'AbortError' || errorLike.code === 'ABORT_ERR';
}

function extractVersionPolicyHeaderStatus(
	response: Response,
): VersionPolicyHeaderStatus | undefined {
	const policyHeader = response.headers.get('X-DS-Version-Policy');
	if (!policyHeader) return undefined;

	return parseVersionPolicyHeaderValue(policyHeader).status;
}

function isHandledVersionPolicyResponse(response: Response, errorKind?: string): boolean {
	const headerStatus = extractVersionPolicyHeaderStatus(response);
	if (headerStatus === 'required' || headerStatus === 'policy-unavailable') {
		return true;
	}

	if (response.status === 426) {
		return true;
	}

	if (response.status === 503 && errorKind === 'VERSION_POLICY_UNAVAILABLE') {
		return true;
	}

	return false;
}

export function init() {
	useCredentialsIncludeRequestInterceptor();

	apiClient.interceptors.request.use((req) => {
		const headers = new Headers(req.headers);
		headers.set('X-DS-Version', versionRequestHeaderValue);
		return new Request(req, { headers });
	});

	apiClient.interceptors.request.use((req, _) => {
		return maybeDelay(Promise.resolve(req));
	});

	apiClient.interceptors.response.use((res) => {
		if (res.ok) {
			versionPolicy.applyResponse(res, undefined, get(authUser)?.id);
		}
		return res;
	});

	apiClient.interceptors.error.use((err, res, req, opts) => {
		const errorKind = domainErrorKind(err);
		const userId = get(authUser)?.id;

		if (!res && isAbortError(err, req)) {
			return err;
		}

		if (res) {
			versionPolicy.applyResponse(res, errorKind, userId);

			if (isHandledVersionPolicyResponse(res, errorKind)) {
				// Let the global version-policy gate own the UX for these states without
				// breaking throwOnError callers that rely on aborted control flow.
				if (opts.throwOnError) {
					const message =
						res.status === 426
							? 'A fresh version of Dream Studio is ready. Reload to get the latest updates.'
							: 'Version policy could not be loaded on the server.';
					return error(res.status, message);
				}

				return err;
			}
		}

		// A paid run (image, sound, music, voice) the billing gate refused: the app says what to do
		// about it (`RefusedRunDialog`), on top of the caller's own message, so no generic toast.
		const refusedPaidRun = refusedRunOf(req, res, err);
		if (refusedPaidRun) {
			refusedRun.set(refusedPaidRun);
			return err;
		}

		// Check for Redirect responses, and errors after an OK response (e.g. a failed transformer)
		if (
			res &&
			((res.status >= 300 && res.status < 400) ||
				(res.status === 0 && res.type === 'opaqueredirect') ||
				res.ok)
		) {
			// Just let the caller handle it
			return err;
		}

		// `meta` isn't in Hey API's resolved options type, but SDK calls pass it through.
		const mode = apiErrorMode((opts as { meta?: ClientMeta }).meta?.errors, err, res);

		const consoleFn = opts.throwOnError ? console.error : console.warn;
		if (isLocal) {
			consoleFn(
				`Error status: ${res?.status}, mode: ${mode}, throwOnError: ${opts.throwOnError}:`,
				err,
			);
			if (Object.hasOwn(err as any, 'stack')) {
				consoleFn((err as any).stack.toString());
			}
		}

		const requestDescription = req ? `${req.method} ${req.url}` : '(UNKNOWN REQUEST)';

		switch (mode) {
			case 'silent':
				break;
			case 'signout': {
				// Do a regular signout that will redirect to login page and clear state
				const { pathname, search, hash } = window.location;
				signOut({ redirectTo: pathname + search + hash });
				return err;
			}
			case 'full':
				errorStore.addError(
					res ? new AppError(res.status, `API error: ${requestDescription}`, err) : (err as Error),
				);
				break;
			case 'toast':
				// One toast per failing endpoint, however often it's retried or polled
				if (res)
					toast.warning(isLocal ? describeError(err).message : 'There was an error.', {
						id: `api-error:${req?.method} ${req ? new URL(req.url).pathname : ''}`,
					});
				// Most likely a network error; one toast for however many requests failed
				else
					toast.warning(
						isLocal ? `Network error: ${requestDescription}` : 'Network error occurred.',
						{ id: 'api-network-error' },
					);
				break;
		}

		if (!opts.throwOnError) return err;
		// We throw a SvelteKit error in case the caller is following a regular pattern.
		if (!res) throw error(500, 'Network error occurred');
		const described = describeError(err);
		throw error(res.status, {
			message: described.message || res.statusText,
			request: requestDescription,
			details: described.details,
		} as any);
	});

	// Refresh user on init if signed in
	refreshUser();
}
